/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import ContactCard from '../../../components/ContactCard';
import { Loader, LoadingBox } from '../../../components/Loader';
import Pagination, { PaginationDetails } from '../../../components/Pagination';
import { ListAndFiltersPageBodyContainer } from '../../../components/layout/PageContainer';
import { SORTING_OPTIONS, PAGINATION_DATA_LIMIT } from '../../../util/consts';
import { ProductCard } from '../components/ProductCard';
import ProductList from '../components/ProductList';
import RequestBulkHelperMessage from '../../quotes/components/HelperMessage/RequestBulkHelperMessage';
import { isBulkQuotable } from '../utils/isBulkQuotable';
import { DateRangeType } from '../../../components/DateRangePicker';
import NoPastProducts from './NoPastProducts';
import ProductListContainer from './ProductsListContainer';
import { getActivePricesForPastProducts, Product } from '../storage';
import { RelationshipOwner, selectCurrentFarmerAccount } from '../../azure-b2c/storage';
import { getFilterCategories } from '../utils/getFilterCategories';
import useStockAvailabilities from '../components/StockAvailability/useStockAvailabilities';
import { useBranch } from '../../../util/useBranch';
import { trackSearch } from '../../../services/adobe/events';
import { useSelector } from 'react-redux';

interface PastProductProps {
  setCategoriesCallback: (categories: string[]) => void;
  paginationCounter: React.ReactNode;
  status: string;
  data: Product[];
  filteredProducts: Product[];
  handleClearFilters: () => void;
  isFilterApplied: boolean;
  sortBy: string;
  setSortBy: React.Dispatch<React.SetStateAction<string>>;
  paginatedData: Product[];
  selectedFilters: {
    category: { value: string; setValue: React.Dispatch<React.SetStateAction<string>> };
    dateRange: {
      value: DateRangeType;
      setValue: React.Dispatch<React.SetStateAction<DateRangeType>>;
    };
    searchText: { value: string; setValue: React.Dispatch<React.SetStateAction<string>> };
    searchInput: { value: string; setValue: React.Dispatch<React.SetStateAction<string>> };
  };
  noPagination: boolean;
  currentPage: number;
  relationshipOwner: RelationshipOwner;
}

const PastProductsTab: React.FC<PastProductProps> = ({
  setCategoriesCallback,
  paginationCounter,
  status,
  data,
  filteredProducts,
  handleClearFilters,
  isFilterApplied,
  sortBy,
  paginatedData,
  selectedFilters,
  noPagination,
  currentPage,
  relationshipOwner,
  setSortBy,
}) => {
  const branchId = useBranch();
  const [products, setProducts] = React.useState<Product[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const selectedAccount = useSelector(selectCurrentFarmerAccount);
  const paginatedDataWithStock = useStockAvailabilities({
    products,
    setState: setLoading,
  });

  useEffect(() => {
    setCategoriesCallback(getFilterCategories(data));
  }, [data]);

  useEffect(() => {
    if (paginatedData.length > 0 && data.length > 0) {
      trackSearch(selectedFilters.searchText.value, data.length);
      (async () => {
        setLoading(true);
        const updatedProducts = await getActivePricesForPastProducts(
          selectedAccount?.accountNumber,
          paginatedData,
          branchId,
        );
        if (updatedProducts) setProducts(updatedProducts);
      })();
    } else {
      setProducts([]);
    }
  }, [paginatedData, branchId]);

  return (
    <ListAndFiltersPageBodyContainer>
      {((status === 'pending' && data.length === 0) || loading) && (
        <LoadingBox>
          <Loader title='Loading your products' type='inline-flex' sx={{ marginTop: '12px' }} />
        </LoadingBox>
      )}
      {data.length > 0 && !loading && (
        <ProductListContainer shrink={paginatedData.length <= 0}>
          <PaginationDetails
            onClear={handleClearFilters}
            showClear={isFilterApplied}
            paginationCounter={paginationCounter}
            sorting={{
              value: sortBy,
              onChange: setSortBy,
              options: SORTING_OPTIONS,
            }}
          />
          <ProductList
            items={paginatedDataWithStock}
            renderListItem={(item) => {
              if (
                isBulkQuotable(selectedFilters.category.value) &&
                item.isPriceNegotiable &&
                selectedAccount?.relationshipOwner?.email &&
                isBulkQuotable(item.category) &&
                paginatedData.indexOf(item) <=
                  paginatedData.findIndex((e) => isBulkQuotable(e.category))
              ) {
                return (
                  <Box key={item.productId}>
                    <RequestBulkHelperMessage show />
                    <ProductCard
                      item={item}
                      enableQuote={!!selectedAccount?.relationshipOwner?.fullName}
                      isPastProduct
                    />
                  </Box>
                );
              }
              return (
                <ProductCard
                  key={item.productId}
                  item={item}
                  enableQuote={!!selectedAccount?.relationshipOwner?.fullName}
                  isPastProduct
                />
              );
            }}
          />
          {!noPagination && (
            <Pagination
              page={currentPage}
              path='/products'
              count={Math.ceil(filteredProducts.length / PAGINATION_DATA_LIMIT)}
            />
          )}
          {(paginatedData.length < 25 || paginatedDataWithStock.length < 25) && (
            <ContactCard
              title={`Can't find what you're looking for?\nContact your Farmlands representative.`}
              sx={{ minWidth: '100%' }}
              {...relationshipOwner}
            />
          )}
        </ProductListContainer>
      )}

      {data.length === 0 && status === 'fulfilled' && <NoPastProducts />}
    </ListAndFiltersPageBodyContainer>
  );
};

export default PastProductsTab;
